/**
 * 🎨 Theme Manager
 *
 * Централизованное управление темами.
 * Каждая тема полностью самодостаточна - содержит все цвета напрямую.
 *
 * Архитектура без primitives:
 * - Каждая тема в отдельном файле
 * - Все цвета определены напрямую (HEX/RGBA)
 * - Нет зависимостей между темами
 */

import type { ThemeTokens } from './semantic';

// Импорт всех тем из папки themes/
import {
  lightTheme,
  darkTheme,
  pinkTheme,
  oliveTheme,
} from './themes/index';

// Типы тем - все доступные имена тем
export type ThemeName = 'light' | 'dark' | 'pink' | 'olive' | 'auto';

function getSystemThemeName(): Exclude<ThemeName, 'pink' | 'olive'> {
  if (typeof window !== 'undefined' && window.matchMedia) {
    try {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch {
      // ignore
    }
  }
  return 'light';
}

// Экспорт типа для обратной совместимости
export type { ThemeTokens };

// ============================================
// 📋 THEMES COLLECTION
// ============================================
// Автоматически собирает все доступные темы
export const themes: Record<ThemeName, ThemeTokens> = {
  light: lightTheme,
  dark: darkTheme,
  pink: pinkTheme,
  olive: oliveTheme,
  auto: getSystemThemeName() === 'dark' ? darkTheme : lightTheme,
} as const;

// ============================================
// 🔍 THEME UTILITIES
// ============================================

/**
 * Получить список всех доступных имен тем
 */
export const availableThemeNames: ThemeName[] = Object.keys(themes) as ThemeName[];

/**
 * Проверить, существует ли тема с данным именем
 */
export function isValidThemeName(name: string): name is ThemeName {
  return name in themes;
}

/**
 * Получить тему по имени с проверкой
 */
export function getThemeByName(name: ThemeName): ThemeTokens {
  return themes[name];
}
